import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Sparkles, Plus, ArrowUpRight } from "lucide-react";
import { api } from "../../lib/api";
import { SkeletonCardList } from "../../components/skeleton";
import { EmptyState } from "../../components/empty-state";
import { OnboardingChecklist } from "../../components/onboarding-checklist";
import { WelcomeModal } from "../../components/welcome-modal";
import { StartTrialBanner } from "../../components/start-trial-banner";
import type { MeResponse } from "../../lib/auth";
import { useToast } from "../../lib/toast";
import { apiErrorMessage } from "../../lib/errors";

interface ProjectRow {
  id: string;
  title: string | null;
  status: string;
  created_at: number;
}

export default function DashboardPage() {
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const ctx = useOutletContext<{ me: MeResponse } | null>();

  useEffect(() => {
    api
      .get<{ projects: ProjectRow[] }>("/api/projects")
      .then((res) => setProjects(res.projects))
      .catch((err) => toast.error(apiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [toast]);

  return (
    <div>
      <WelcomeModal userName={ctx?.me?.user?.name ?? null} />
      <StartTrialBanner plan={ctx?.me?.credits?.plan} />
      <OnboardingChecklist />
      <div className="mb-7 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="page-title">Projects</h1>
          <p className="page-subtitle">
            Paste long-form content; we'll turn it into native variants for X,
            Bluesky, LinkedIn, Newsletter, and short-video.
          </p>
        </div>
        <Link to="/app/new" className="btn-brand no-underline">
          <Plus className="h-4 w-4" />
          New project
        </Link>
      </div>

      {loading ? (
        <SkeletonCardList count={4} />
      ) : projects.length === 0 ? (
        <EmptyState
          icon={<Sparkles className="h-6 w-6" />}
          title="No projects yet"
          description="Paste any long-form post and BrandEcho will turn it into native variants for X, Bluesky, LinkedIn, Newsletter, and short-video."
          action={
            <Link to="/app/new" className="btn-brand no-underline">
              <Plus className="h-4 w-4" />
              Start your first project
            </Link>
          }
        />
      ) : (
        <ul className="space-y-3">
          {projects.map((p) => (
            <li key={p.id}>
              <Link
                to={`/app/projects/${p.id}`}
                className="group flex items-center justify-between gap-4 rounded-xl border border-ink-200/70 bg-white p-5 shadow-xs no-underline transition-all hover:-translate-y-0.5 hover:border-ink-200 hover:shadow-md"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-ink-900">
                    {p.title ?? "Untitled"}
                  </p>
                  <p className="mt-0.5 text-xs text-ink-500">
                    {new Date(p.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <StatusPill status={p.status} />
                  <ArrowUpRight className="h-4 w-4 text-ink-300 transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-ink-700" />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const colors: Record<string, string> = {
    queued: "badge-neutral",
    generating: "badge-info",
    ready: "badge-success",
    failed: "badge-danger",
  };
  return <span className={colors[status] ?? "badge-neutral"}>{status}</span>;
}
